import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'utils/i18n';
import { useAuth } from 'contexts/AuthContext';
import { useUserType, VIEW_MODES } from 'utils/userTypeContext';
import { MarketplaceModule } from 'components/marketplace';
import { getApiClient } from 'utils/apiClient';
import translations from './translations';
import marketplaceTranslations from 'components/marketplace/translations';
import './Marketplace.css';
import '../../components/ResourceDetailModal/styles/MarketplaceSelection.css';

/**
 * Marketplace Page
 * Standalone marketplace page using MarketplaceModule
 */
const Marketplace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { viewMode: userViewMode } = useUserType();
  const isCustomerMode = userViewMode === VIEW_MODES.NORMAL;
  const { language } = useTranslation();
  const pageTranslations = translations[language] || translations.en || translations.de;
  const marketplaceT = (key, defaultValue = '') => {
    const keys = key.split('.');
    let value = marketplaceTranslations[language] || marketplaceTranslations.en;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || defaultValue || key;
  };
  const t = (key, defaultValue = '') => {
    const keys = key.split('.');
    let value = pageTranslations;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || defaultValue || key;
  };
  
  const [costPools, setCostPools] = useState([]);
  const [loadingCostPools, setLoadingCostPools] = useState(false);
  const [hasValidPaymentMethod, setHasValidPaymentMethod] = useState(false);
  const loadingRef = useRef(false);
  const circuitBreakerOpenRef = useRef(false);
  const [headerRightEl, setHeaderRightEl] = useState(null);
  const headerRightRef = useCallback((el) => {
    setHeaderRightEl(el);
  }, []);

  // Redirect only when URL has partner param but no product (incomplete partner flow on marketplace).
  // Do NOT redirect when user clicked "Browse Marketplace" from dashboard (/marketplace with no params).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasPartner = params.has('partner');
    const hasProduct = params.has('product');
    if (hasPartner && !hasProduct) {
      navigate('/');
    }
  }, [location.search, navigate]);

  // Load cost pools and check payment methods for customer mode users
  useEffect(() => {
    if (isCustomerMode && isAuthenticated) {
      checkPaymentMethods();
    } else {
      setHasValidPaymentMethod(false);
      setCostPools([]);
      setLoadingCostPools(false);
      loadingRef.current = false;
      circuitBreakerOpenRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomerMode, isAuthenticated]);

  const checkPaymentMethods = async () => {
    // Prevent duplicate calls
    if (loadingRef.current) {
      return;
    }
    
    // If circuit breaker is open, don't retry
    if (circuitBreakerOpenRef.current) {
      console.warn('Circuit breaker is open for /v1/cost-pools, skipping request');
      setHasValidPaymentMethod(false);
      setCostPools([]);
      setLoadingCostPools(false);
      return;
    }
    
    try {
      loadingRef.current = true;
      setLoadingCostPools(true);
      const apiClient = getApiClient();
      
      // Load only cost pools that have a valid payment method — backend filters on valid_payment_method=true
      const response = await apiClient.listCostPools({ requirePaymentMethod: true });
      
      let allCostPools = [];
      if (response && response.success && response.data) {
        allCostPools = Array.isArray(response.data) ? response.data : [];
      } else if (Array.isArray(response)) {
        allCostPools = response;
      } else if (response && Array.isArray(response.data)) {
        allCostPools = response.data;
      }
      
      setCostPools(allCostPools);
      
      // Backend already filtered to only pools with valid_payment_method=true,
      // so any returned pool is usable.
      setHasValidPaymentMethod(allCostPools.length > 0);
      circuitBreakerOpenRef.current = false; // Reset circuit breaker flag on success
    } catch (err) {
      // Check if this is a circuit breaker error
      const isCircuitBreakerError = err?.message?.includes('Circuit breaker open') || 
                                    err?.message?.includes('circuit breaker');
      
      if (isCircuitBreakerError) {
        console.warn('Circuit breaker is open for /v1/cost-pools:', err.message);
        circuitBreakerOpenRef.current = true;
      } else {
        console.error('Error checking payment methods:', err);
      }
      
      setHasValidPaymentMethod(false);
      setCostPools([]);
    } finally {
      loadingRef.current = false;
      setLoadingCostPools(false);
    }
  };

  // Don't render (show nothing briefly) only when we're redirecting: URL has partner but no product
  const params = new URLSearchParams(location.search);
  const hasPartner = params.has('partner');
  const hasProduct = params.has('product');
  if (hasPartner && !hasProduct) {
    return null;
  }

  return (
    <div className="marketplace-page-wrapper" data-onboarding-target="marketplace-page">
      <div className="marketplace-page-header">
        <div className="marketplace-header-left">
          <h1 className="marketplace-page-title">{t('title', 'Marketplace')}</h1>
          <p className="marketplace-page-subtitle">
            {t('subtitle', 'Discover partners, add-ons and solutions for your B-esper platform')}
          </p>
        </div>
        <div ref={headerRightRef} className="marketplace-header-center" />
        <div className="marketplace-header-right">
          {isCustomerMode && isAuthenticated && !loadingCostPools && !hasValidPaymentMethod && (
            <div className="marketplace-payment-warning-header">
              <div className="marketplace-payment-warning-content">
                <div className="marketplace-payment-warning-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </div>
                <div className="marketplace-payment-warning-text">
                  <h4 className="marketplace-payment-warning-title">
                    {marketplaceT('paymentWarning.title', 'Payment Method Required')}
                  </h4>
                  <p className="marketplace-payment-warning-message">
                    {marketplaceT('paymentWarning.message', 'You need to add a valid payment method to your cost pool before you can purchase products. Please set up your payment profile first.')}
                  </p>
                </div>
                <button
                  className="marketplace-payment-warning-button"
                  onClick={() => {
                    const firstCostPool = costPools && costPools.length > 0 ? costPools[0] : null;
                    if (firstCostPool && firstCostPool.id) {
                      navigate(`/payment-profile/${firstCostPool.id}?tab=payment-methods`);
                    } else {
                      navigate('/payment-profile?tab=payment-methods');
                    }
                  }}
                >
                  {marketplaceT('paymentWarning.action', 'Setup Payment')}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6"></path>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="marketplace-page-content">
        {/* Mobile Payment Warning - Only shown on mobile (header version is for desktop) */}
        {isCustomerMode && isAuthenticated && !loadingCostPools && !hasValidPaymentMethod && (
          <div className="marketplace-payment-warning-header marketplace-payment-warning-mobile">
            <div className="marketplace-payment-warning-content">
              <div className="marketplace-payment-warning-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </div>
              <div className="marketplace-payment-warning-text">
                <h4 className="marketplace-payment-warning-title">
                  {marketplaceT('paymentWarning.title', 'Payment Method Required')}
                </h4>
                <p className="marketplace-payment-warning-message">
                  {marketplaceT('paymentWarning.message', 'You need to add a valid payment method to your cost pool before you can purchase products. Please set up your payment profile first.')}
                </p>
              </div>
              <button
                className="marketplace-payment-warning-button"
                onClick={() => {
                  const firstCostPool = costPools && costPools.length > 0 ? costPools[0] : null;
                  if (firstCostPool && firstCostPool.id) {
                    navigate(`/payment-profile/${firstCostPool.id}?tab=payment-methods`);
                  } else {
                    navigate('/payment-profile?tab=payment-methods');
                  }
                }}
              >
                {marketplaceT('paymentWarning.action', 'Setup Payment')}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6"></path>
                </svg>
              </button>
            </div>
          </div>
        )}

        <MarketplaceModule mode="standalone" headerRightEl={headerRightEl} />
      </div>

    </div>
  );
};

export default Marketplace;
